import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { formatCZK, formatDateShort } from "@/lib/format";
import { PhotoThumb } from "@/components/shared/PhotoLightbox";
import { SupplierMark } from "./OrderBits";
import { OrderChain } from "./OrderChain";

// 329: „Patří k objednávce?“ v detailu přijaté faktury. Platbu zadáváš jednou — na faktuře; spárovaná
// objednávka si stav a datum úhrady jen přečte. Karta se ukáže jen když je co spárovat nebo už je spárováno.
// Uložená faktura páruje hned (API), rozepsaná (nahrání) si výběr drží v `doc.link_order_ids` a spáruje se při uložení.
const paidOn = (doc) => {
  if ((doc.payment_status || "") !== "Zaplaceno") return null;
  const days = (doc.payments || []).map((p) => String(p.date || "").slice(0, 10)).filter(Boolean).sort();
  return days.length ? formatDateShort(days[days.length - 1]) : "ano";
};

function Candidate({ c, picked, saved, busy, onPick, onSkip }) {
  const parts = [c.order_no ? `č. ${c.order_no}` : null, c.amount != null ? formatCZK(c.amount) : null, c.project_name,
    c.status === "received" ? (c.received_at ? `převzato ${formatDateShort(c.received_at)}` : "převzato") : c.status_label,
    c.delivery_note ? "doklad k převzetí ✓" : null].filter(Boolean);
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3" data-testid={`order-cand-${c.id}`}>
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <SupplierMark name={c.supplier} size={36} />
        <div className="min-w-0 flex-1">
          <div className="font-bold text-[14.5px] break-words">{c.title}
            {c.amount_match && <span className="ml-1.5 align-middle text-[11.5px] font-extrabold rounded-md px-1.5 py-0.5 bg-success/10 text-success whitespace-nowrap">sedí částka</span>}
            {!c.supplier_match && <span className="ml-1.5 align-middle text-[11.5px] font-bold rounded-md px-1.5 py-0.5 bg-secondary text-muted-foreground whitespace-nowrap">jiný dodavatel</span>}</div>
          <div className="text-[12.5px] text-muted-foreground break-words">{parts.join(" · ")}</div>
        </div>
      </div>
      {picked ? (
        <Button type="button" size="sm" variant="outline" onClick={onPick} className="h-10 sm:h-9 rounded-full font-bold shrink-0 w-full sm:w-auto" data-testid={`order-unpick-${c.id}`}>✓ Spárováno</Button>
      ) : (
        <div className="flex gap-1.5 shrink-0">
          <Button type="button" size="sm" disabled={busy} onClick={onPick} className="h-10 sm:h-9 rounded-full font-bold flex-1 sm:flex-none" data-testid={`order-pair-${c.id}`}>Spárovat</Button>
          <Button type="button" size="sm" variant="ghost" onClick={onSkip} className="h-10 sm:h-9 rounded-full font-semibold text-muted-foreground" data-testid={`order-skip-${c.id}`}>Není to ono</Button>
        </div>
      )}
    </div>
  );
}

export function OrderLinkCard({ doc, setDoc }) {
  const saved = !!doc.id;
  const [data, setData] = useState({ linked: [], candidates: [] });
  const [skipped, setSkipped] = useState([]);
  const [busy, setBusy] = useState(false);
  const picked = doc.link_order_ids || [];
  const key = saved ? doc.id : [doc.supplier_name, doc.supplier_ico, doc.total_with_vat, doc.total_without_vat].join("|");

  const load = async () => {
    const params = saved ? { document_id: doc.id }
      : { supplier_name: doc.supplier_name || undefined, supplier_ico: doc.supplier_ico || undefined,
        total_with_vat: Number(doc.total_with_vat) || undefined, total_without_vat: Number(doc.total_without_vat) || undefined };
    if (!saved && !params.supplier_name && !params.total_with_vat) { setData({ linked: [], candidates: [] }); return; }
    try { const r = await api.get("/orders/candidates", { params }); setData(r.data); }
    catch { setData({ linked: [], candidates: [] }); }      // bez práva na objednávky karta prostě není
  };
  useEffect(() => {
    const t = setTimeout(load, saved ? 0 : 450);
    return () => clearTimeout(t);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const pair = async (c) => {
    if (!saved) { setDoc((d) => ({ ...d, link_order_ids: picked.includes(c.id) ? picked.filter((x) => x !== c.id) : [...picked, c.id] })); return; }
    setBusy(true);
    try {
      const { data: o } = await api.post(`/orders/${c.id}/link`, { document_id: doc.id });
      toast.success("Objednávka spárována s fakturou.");
      if (o.paid_conflict) toast.message("Objednávka byla označená jako zaplacená, faktura zatím ne — platba se teď bere z faktury.");
      window.dispatchEvent(new Event("pp-order-linked"));
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const unpair = async (c) => {
    setBusy(true);
    try { await api.post(`/orders/${c.id}/unlink`); toast.success("Spárování zrušeno."); window.dispatchEvent(new Event("pp-order-linked")); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const cands = data.candidates.filter((c) => !skipped.includes(c.id));
  if (!data.linked.length && !cands.length) return null;
  const paid = paidOn(doc);
  return (
    <section data-testid="order-link-card">
      <h3 className="text-sm font-semibold text-muted-foreground mb-2">{data.linked.length ? "Objednávka" : "Patří k objednávce?"}</h3>
      <div className="space-y-2.5">
        {data.linked.map((c) => (
          <div key={c.id} className="rounded-[18px] border border-success/30 bg-success/[0.07] p-3 md:p-4" data-testid={`order-linked-${c.id}`}>
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-bold text-success">Spárováno s objednávkou {c.order_no ? `č. ${c.order_no}` : c.title}</div>
                <div className="text-[12.5px] text-muted-foreground">{[c.supplier, c.title, c.received_at ? `převzato ${formatDateShort(c.received_at)}` : c.status_label].filter(Boolean).join(" · ")}</div>
              </div>
              {c.delivery_note_url && <PhotoThumb url={c.delivery_note_url} testid={`order-dodak-${c.id}`} />}
            </div>
            <OrderChain className="mt-2.5" ordered="✓" received={c.status === "received"} note={c.delivery_note > 0} invoice={doc.invoice_number || "ano"} paid={paid} />
            <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => unpair(c)} className="h-9 rounded-full font-bold mt-3" data-testid={`order-unlink-${c.id}`}>Zrušit spárování</Button>
          </div>
        ))}
        {cands.length > 0 && (
          <div className="rounded-[18px] border border-brand/50 bg-brand/[0.04] p-3 md:p-4 space-y-3">
            <p className="text-[13px] text-muted-foreground">{data.linked.length ? "Další objednávky od stejného dodavatele:" : "Našli jsme otevřené objednávky od stejného dodavatele nebo se stejnou částkou:"}</p>
            {cands.map((c) => <Candidate key={c.id} c={c} saved={saved} busy={busy} picked={!saved && picked.includes(c.id)} onPick={() => pair(c)} onSkip={() => setSkipped((s) => [...s, c.id])} />)}
            {!saved && picked.length > 0 && <p className="text-[12.5px] text-success font-semibold">Spáruje se při uložení faktury — platba se pak bere z ní.</p>}
          </div>
        )}
      </div>
    </section>
  );
}
